import { effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    RowClickEditBehavior,
    TableEditMode,
} from '@connect-services/client/components/entity-table/entity-table.component';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeLeaveService } from '@connect-services/client/services/edit-mode-leave.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { EditModeCapabilities } from '@connect-services/client/services/map-edit-mode.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';

@Injectable({
    providedIn: 'root',
})
export class TableEditModeService {
    public readonly currentMode = signal<TableEditMode>(TableEditMode.NONE);
    public readonly isEditModeActive = signal<boolean>(false);
    public readonly isExternalTableEditActive = signal<boolean>(false);

    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly editModeLeaveService = inject(EditModeLeaveService);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router = inject(Router);

    public constructor() {
        effect(() => {
            this.projectEditModeService.isEditModeActive()
                ? this.forceEnableEditModeGlobal()
                : this.forceDisableEditModeGlobal();
        });
    }

    public activateEditMode(initialMode: TableEditMode): void {
        this.isEditModeActive.set(true);
        this.currentMode.set(initialMode);
        this.editEntityDetailService.isEditMode.set(true);
        this.projectEditModeService.activateEditMode();
    }

    public async ensureCanLeaveTableEditMode(): Promise<boolean> {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return true;
        }

        const hasCreationContext = this.hasCreationContext();
        const pendingCount = this.editModeStateService.getPendingEditCount(projectId);

        if (hasCreationContext && !pendingCount) {
            this.entityDetailCreationContextService.disableAutoSave();

            await this.finalizeTableEditModeExit();

            return true;
        }

        if (!this.isEditModeActive() && !pendingCount) {
            await this.closeEntityDetailPanelIfOpen();
            this.entityDetailCreationContextService.reset();

            return true;
        }

        if (!pendingCount) {
            await this.finalizeTableEditModeExit(true);

            return true;
        }

        const result = await this.editModeLeaveService.requestLeaveEditMode({
            projectId,
        });

        if (!result.canLeave) {
            this.isEditModeActive.set(true);
            this.editEntityDetailService.isEditMode.set(true);

            return false;
        }

        if (hasCreationContext) {
            this.entityDetailCreationContextService.disableAutoSave();
        }

        await this.finalizeTableEditModeExit();

        return true;
    }

    public enterEntityDetailEditMode(entityId: string): void {
        this.editEntityDetailService.isEditMode.set(true);
        this.editEntityDetailService.entityId.set(entityId);
    }

    public enterTableEditModeExternalView(capabilities: EditModeCapabilities, entityId?: string | null): boolean {
        if (!capabilities.enabled) {
            return false;
        }

        this.isExternalTableEditActive.set(true);
        this.currentMode.set(TableEditMode.ENTITY_DETAIL);

        if (entityId) {
            this.enterEntityDetailEditMode(entityId);
        }

        return true;
    }

    public exitEntityDetailEditMode(): void {
        this.editEntityDetailService.isEditMode.set(false);
        this.editEntityDetailService.entityId.set('');
    }

    public forceDisableEditModeGlobal(): void {
        this.isEditModeActive.set(false);
        this.currentMode.set(TableEditMode.NONE);
    }

    public forceEnableEditModeGlobal(): void {
        if (!this.isEditModeActive()) {
            this.isEditModeActive.set(true);
            this.editEntityDetailService.isEditMode.set(true);
        }

        if (this.currentMode() !== TableEditMode.NONE) {
            return;
        }

        this.currentMode.set(TableEditMode.ENTITY_DETAIL);
    }

    public forceExitEditModeUi(): void {
        this.exitEntityDetailEditMode();
        this.isExternalTableEditActive.set(false);
        this.forceDisableEditModeGlobal();
    }

    public getRowClickBehavior(capabilities: EditModeCapabilities): RowClickEditBehavior {
        if (!this.isEditModeActive() && !this.isExternalTableEditActive()) {
            return RowClickEditBehavior.OPEN_READ_ONLY_DETAIL;
        }

        if (!capabilities.canUpdate) {
            return RowClickEditBehavior.OPEN_READ_ONLY_DETAIL;
        }

        if (this.currentMode() === TableEditMode.TABLE_EDIT) {
            return RowClickEditBehavior.ENTER_TABLE_EDIT;
        }

        return RowClickEditBehavior.OPEN_EDITABLE_DETAIL;
    }

    public leaveTableEditModeExternalView(): void {
        if (!this.isExternalTableEditActive()) {
            return;
        }

        this.isExternalTableEditActive.set(false);
        this.currentMode.set(TableEditMode.NONE);
        this.exitEntityDetailEditMode();
    }

    public async requestToggleEditMode(
        capabilities: EditModeCapabilities,
        currentPanelEntityId: string | null,
    ): Promise<boolean> {
        if (this.isEditModeActive()) {
            const canLeave = await this.ensureCanLeaveTableEditMode();
            if (!canLeave) {
                return true;
            }

            this.exitEntityDetailEditMode();
            this.deactivateEditMode();

            return false;
        }

        if (!capabilities.enabled) {
            return false;
        }

        const initialMode = capabilities.canUpdate ? TableEditMode.ENTITY_DETAIL : TableEditMode.NONE;

        this.activateEditMode(initialMode);

        if (currentPanelEntityId && initialMode === TableEditMode.ENTITY_DETAIL) {
            this.enterEntityDetailEditMode(currentPanelEntityId);
        }

        return true;
    }

    public updateModeFromToggle(isTableMode: boolean, currentPanelEntityId: string | null): void {
        if (!this.isEditModeActive()) {
            return;
        }

        this.applyToggleMode(isTableMode, currentPanelEntityId);
    }

    public updateModeFromToggleExternalView(isTableMode: boolean, entityId: string | null): void {
        if (!this.isExternalTableEditActive()) {
            return;
        }

        this.applyToggleMode(isTableMode, entityId);
    }

    private applyToggleMode(isTableMode: boolean, entityId: string | null): void {
        if (isTableMode) {
            this.currentMode.set(TableEditMode.TABLE_EDIT);
            this.exitEntityDetailEditMode();

            return;
        }

        this.currentMode.set(TableEditMode.ENTITY_DETAIL);
        entityId ? this.enterEntityDetailEditMode(entityId) : this.exitEntityDetailEditMode();
    }

    private async closeEntityDetailPanelIfOpen(): Promise<void> {
        const hasEntityDetailPanel =
            this.router.url.includes(RouteNameEnum.ENTITY_DETAIL) ||
            this.router.url.includes(RoutePathEnum.TABLE_ENTITY_DETAIL);

        if (!hasEntityDetailPanel) {
            return;
        }

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        await this.router.navigate([RoutePathEnum.PROJECTS, projectId]);
    }

    private deactivateEditMode(): void {
        this.isEditModeActive.set(false);
        this.currentMode.set(TableEditMode.NONE);
        this.editEntityDetailService.isEditMode.set(false);
        this.projectEditModeService.deactivateEditMode();
    }

    private async finalizeTableEditModeExit(deactivateProjectEditMode = false): Promise<void> {
        this.exitEntityDetailEditMode();
        this.forceDisableEditModeGlobal();

        if (deactivateProjectEditMode) {
            this.projectEditModeService.deactivateEditMode();
        }

        await this.closeEntityDetailPanelIfOpen();

        this.entityDetailCreationContextService.reset();
    }

    private hasCreationContext(): boolean {
        const entityId = this.entityDetailCreationContextService.entityId();

        if (!entityId) {
            return false;
        }

        return (
            !!this.entityDetailCreationContextService.source() ||
            this.entityDetailCreationContextService.isEditingExistingCreatedEntity()
        );
    }
}
